import moment from 'moment'
import { cloneDeep, each, get, keys, set, unset } from 'lodash'
import { createTransform } from 'redux-persist'

import { getTimeZoneFromEvent } from '../Event/helpers/encodeOccurrence'
import { isKioskSessionLifecycleEnabledFromPersist } from '../kiosk/kioskSessionLifecycleFeature'
import kioskPersistVolatileFields from './kioskPersistVolatileFields'

const RESUMABLE_STATUS = 'active'

// When KioskSessionLifecycle is off, inbound/outbound are pass-through (prod kiosk persist).
// When on, strip session-scoped fields and restore status + opportunity only if resumable.

export function isKioskResumable(opportunity) {
  if (!opportunity?.startsAt) {
    return false
  }

  const timeZone = getTimeZoneFromEvent(opportunity)
  const now = moment.tz(timeZone)
  const start = moment.tz(opportunity.startsAt, timeZone)

  if (now.isBefore(start)) {
    return false
  }

  // Same-calendar-day occurrence (typical event kiosk).
  if (start.format('YYYY-MM-DD') === now.format('YYYY-MM-DD')) {
    return true
  }

  // Long-running location-style kiosk: no end date, or still within the overall window.
  if (!opportunity.endsAt) {
    return true
  }

  return now.isSameOrBefore(moment.tz(opportunity.endsAt, timeZone))
}

function stripVolatileFields(state, { keepResumeState = false } = {}) {
  const nextState = cloneDeep(state)

  keys(kioskPersistVolatileFields).forEach(path => {
    if (keepResumeState && (path === 'status' || path === 'opportunity')) {
      return
    }
    unset(nextState, path)
  })

  return nextState
}

function applyVolatileDefaults(state, { resumeOpportunity = null } = {}) {
  const nextState = cloneDeep(state)

  each(kioskPersistVolatileFields, (defaultValue, path) => {
    if (resumeOpportunity && (path === 'status' || path === 'opportunity')) {
      return
    }
    set(nextState, path, defaultValue)
  })

  if (resumeOpportunity) {
    set(nextState, 'status', RESUMABLE_STATUS)
    set(nextState, 'opportunity', resumeOpportunity)
  }

  return nextState
}

// The switch can be on when state is written and off when it is read (e.g. another org or
// Cognito pool), so fields stripped on write would otherwise rehydrate as undefined.
function fillMissingVolatileDefaults(state) {
  const nextState = cloneDeep(state)

  each(kioskPersistVolatileFields, (defaultValue, path) => {
    if (get(nextState, path) === undefined) {
      set(nextState, path, defaultValue)
    }
  })

  return nextState
}

export default createTransform(
  inboundState => {
    if (!isKioskSessionLifecycleEnabledFromPersist()) {
      return inboundState
    }

    const keepResumeState = inboundState?.status === RESUMABLE_STATUS && inboundState?.opportunity
    return stripVolatileFields(inboundState, { keepResumeState })
  },
  outboundState => {
    if (!isKioskSessionLifecycleEnabledFromPersist()) {
      return fillMissingVolatileDefaults(outboundState)
    }

    const resumeOpportunity =
      outboundState?.status === RESUMABLE_STATUS &&
      outboundState?.opportunity &&
      isKioskResumable(outboundState.opportunity)
        ? outboundState.opportunity
        : null

    return applyVolatileDefaults(outboundState, { resumeOpportunity })
  },
  { whitelist: ['kiosk'] },
)
